import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Building2, CheckCircle2, FileCheck2, Lock, Plus, Send, Trash2 } from 'lucide-react';

const API = (process.env.REACT_APP_API_URL || '/api').replace(/\/$/, '');

const initial = {
  tipo_pessoa: 'PF', nome: '', razao_social: '', nome_fantasia: '', cpf_cnpj: '', cpf_socio: '',
  telefone_principal: '', email: '', doc_tipo: 'CNH', rg_numero: '', estado_rg: '', expedicao_rg: '', rg_tem_cpf: 'sim',
  senha_serasa: '', titulo_eleitor: '', sexo: '', data_nascimento: '', nome_pai: '', nome_mae: '', escolaridade: '',
  profissao: '', media_salarial: '', dia_pagamento: '', estado_civil_texto: '', conjuge_nome: '', conjuge_cpf: '',
  conjuge_nascimento: '', dependentes: '0', cep: '', endereco: '', bairro: '', municipio: '', estado: '',
  telefone_residencial: '', telefone_comercial: '', outros_telefones_numeros: '', situacao_imovel: '',
  localizacao_imovel_alugado: '', possui_imoveis: 'nao', imoveis: [], veiculos: []
};

const steps = ['Identificação', 'Documentos', 'Dados pessoais', 'Endereço e patrimônio', 'Conferência'];

function Field({ label, required, children, help }) {
  return (
    <label className="block">
      <span className="block text-sm font-semibold text-slate-700 mb-1.5">{label}{required && <span className="text-red-500"> *</span>}</span>
      {children}
      {help && <span className="block text-xs text-slate-500 mt-1">{help}</span>}
    </label>
  );
}

const inputClass = 'w-full rounded-xl border border-slate-300 bg-white px-3.5 py-3 text-slate-900 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100';

export default function PartnerIntake() {
  const { token } = useParams();

  // FINANCY_LINK_PUBLICO_FRONT
  const coletaFinancy =
    /^\/coleta-financy\/?$/.test(
      window.location.pathname
    );

  const tokenColeta =
    coletaFinancy
      ? 'financy'
      : token;

  const [partner, setPartner] = useState('');
  const [data, setData] = useState(initial);
  const [files, setFiles] = useState({});
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(null);
  const [consent, setConsent] = useState(false);

  // FINANCY_FLUXO_COMERCIAL_FRONT
  const [financyEtapa, setFinancyEtapa] =
    useState(
      coletaFinancy
        ? 'CONTRATACAO'
        : 'COLETA'
    );

  const [financyForma, setFinancyForma] =
    useState('');

  const [financyLeadId, setFinancyLeadId] =
    useState(null);

  const [financyContratoUrl, setFinancyContratoUrl] =
    useState('');

  const [financyStatus, setFinancyStatus] =
    useState(null);

  const [financyProcessando, setFinancyProcessando] =
    useState(false);

  useEffect(() => {
    fetch(`${API}/partner-intake/${tokenColeta}`)
      .then(async response => {
        const body = await response.json();
        if (!response.ok) throw new Error(body.error || 'Link inválido ou desativado.');
        setPartner(body.parceiro.nome);
      })
      .catch(err => setError(err.message))
      .finally(() => setLoading(false));
  }, [tokenColeta]);

  const update = (key, value) => setData(current => ({ ...current, [key]: value }));
  const updateArray = (key, index, field, value) => setData(current => ({
    ...current,
    [key]: current[key].map((item, i) => i === index ? { ...item, [field]: value } : item)
  }));
  const addProperty = () => setData(current => ({ ...current, imoveis: [...current.imoveis, { tipo: 'Casa', localizacao: '', valor: '' }] }));
  const addVehicle = () => setData(current => ({ ...current, veiculos: [...current.veiculos, { tipo: 'Carro', valor: '', ano: '', placa: '', estado_placa: '' }] }));
  const removeItem = (key, index) => setData(current => ({ ...current, [key]: current[key].filter((_item, i) => i !== index) }));

  const cpfLabel = data.tipo_pessoa === 'PF' ? 'CPF' : 'CNPJ da empresa';
  const review = useMemo(() => [
    ['Tipo', data.tipo_pessoa], ['Nome', data.nome], [cpfLabel, data.cpf_cnpj],
    ...(data.tipo_pessoa !== 'PF' ? [['CPF do sócio administrador', data.cpf_socio], ['Razão social', data.razao_social]] : []),
    ['Celular', data.telefone_principal], ['E-mail', data.email], ['Documento', data.doc_tipo],
    ['Número do RG', data.rg_numero || 'Não informado'], ['Estado do RG', data.estado_rg || 'Não informado'],
    ['Expedição do RG', data.expedicao_rg || 'Não informada'], ['Senha SERASA', data.senha_serasa ? 'Informada' : 'Pendente'],
    ['Título de eleitor', data.titulo_eleitor], ['Sexo', data.sexo], ['Nascimento', data.data_nascimento],
    ['Nome do pai', data.nome_pai], ['Nome da mãe', data.nome_mae], ['Estado civil', data.estado_civil_texto],
    ['Escolaridade', data.escolaridade], ['Profissão', data.profissao], ['Média salarial', data.media_salarial],
    ['CEP', data.cep], ['Endereço', data.endereco], ['Bairro', data.bairro], ['Município', data.municipio], ['Estado', data.estado]
  ], [data, cpfLabel]);

  function validateStep() {
    if (step === 0) {
      if (!data.nome.trim() || !data.cpf_cnpj.trim() || !data.telefone_principal.trim() || !data.email.trim()) return 'Preencha nome, documento, celular e e-mail.';
      if (data.tipo_pessoa !== 'PF' && !data.cpf_socio.trim()) return 'Informe o CPF do sócio administrador.';
    }
    if (step === 1) {
      if (!files.documento_frente || !files.selfie_documento || !files.comprovante_endereco) return 'Envie a frente do documento, a selfie e o comprovante de endereço.';
      if (data.doc_tipo === 'RG' && !files.documento_verso) return 'Envie o verso do RG.';
      if (data.doc_tipo === 'RG' && data.rg_tem_cpf === 'nao' && !files.cpf_documento) return 'Envie também o documento do CPF.';
    }
    if (step === 2) {
      if (!data.senha_serasa || !data.escolaridade || !data.profissao || !data.media_salarial || !data.estado_civil_texto) return 'Preencha todos os campos obrigatórios desta etapa.';
      if (data.estado_civil_texto === 'Casado' && (!data.conjuge_nome || !data.conjuge_cpf || !files.certidao_casamento)) return 'Para pessoa casada, informe os dados do cônjuge e envie a certidão.';
    }
    if (step === 3) {
      if (!data.cep || !data.endereco || !data.municipio || !data.estado) return 'Preencha CEP, endereço, município e estado.';
        if (!data.situacao_imovel) return 'Selecione o tipo de imóvel.';
      if (
          data.possui_imoveis === 'sim' &&
          data.imoveis.length === 0
        ) {
          return 'Você informou que possui imóvel. Adicione pelo menos um imóvel.';
        }
    }
    return '';
  }

  function next() {
    const message = validateStep();
    if (message) { setError(message); window.scrollTo({ top: 0, behavior: 'smooth' }); return; }
    setError('');
    setStep(current => Math.min(current + 1, steps.length - 1));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  async function iniciarContratacaoFinancy() {
    if (!financyForma) {
      setError(
        'Selecione uma forma de pagamento.'
      );
      return;
    }

    if (
      !data.nome.trim() ||
      !data.cpf_cnpj.trim() ||
      !data.telefone_principal.trim() ||
      !data.email.trim()
    ) {
      setError(
        'Preencha nome, documento, celular e e-mail.'
      );
      return;
    }

    if (
      data.tipo_pessoa !== 'PF' &&
      !data.cpf_socio.trim()
    ) {
      setError(
        'Informe o CPF do sócio administrador.'
      );
      return;
    }

    try {
      setFinancyProcessando(true);
      setError('');

      const response =
        await fetch(
          `${API}/partner-intake/financy/iniciar`,
          {
            method: 'POST',
            headers: {
              'Content-Type':
                'application/json'
            },
            body: JSON.stringify({
              tipo_pessoa:
                data.tipo_pessoa,

              forma_pagamento:
                financyForma,

              nome:
                data.nome,

              email:
                data.email,

              telefone:
                data.telefone_principal,

              cpf_cnpj:
                data.cpf_cnpj,

              cpf_socio:
                data.cpf_socio,

              razao_social:
                data.razao_social,

              nome_fantasia:
                data.nome_fantasia
            })
          }
        );

      const result =
        await response.json();

      if (!response.ok) {
        throw new Error(
          result.error ||
          'Não foi possível iniciar a contratação.'
        );
      }

      setFinancyLeadId(
        result.lead_id
      );

      /*
       * FINANCY_FRONT_RETOMAR_PAGAMENTO_V1
       *
       * A rota /financy/iniciar pode retomar
       * uma contratação existente diretamente
       * em ASSINATURA, PAGAMENTO ou COLETA.
       */
      if (
        result.etapa === 'COLETA'
      ) {
        setFinancyEtapa(
          'COLETA'
        );
        return;
      }

      if (
        result.etapa === 'PAGAMENTO'
      ) {
        setFinancyEtapa(
          'PAGAMENTO'
        );

        /*
         * Carregar imediatamente os dados
         * da cobrança já existente.
         */
        try {
          const statusResponse =
            await fetch(
              `${API}/partner-intake/financy/status/${result.lead_id}`
            );

          const statusResult =
            await statusResponse.json();

          if (
            statusResponse.ok
          ) {
            setFinancyStatus(
              statusResult
            );

            if (
              statusResult.etapa ===
                'COLETA'
            ) {
              setFinancyEtapa(
                'COLETA'
              );
            }
          }
        } catch (_error) {
          /*
           * A tela de pagamento permanece ativa.
           * O botão manual continua disponível.
           */
        }

        return;
      }

      const assinaturaUrl =
        result.contrato
          ?.url_assinatura ||
        '';

      setFinancyContratoUrl(
        assinaturaUrl
      );

      setFinancyEtapa(
        'ASSINATURA'
      );

    } catch (err) {
      setError(
        err.message ||
        'Não foi possível iniciar a contratação.'
      );
    } finally {
      setFinancyProcessando(false);
    }
  }

  async function consultarStatusFinancy() {
    if (!financyLeadId) {
      return;
    }

    try {
      const response =
        await fetch(
          `${API}/partner-intake/financy/status/${financyLeadId}`
        );

      const result =
        await response.json();

      if (!response.ok) {
        throw new Error(
          result.error ||
          'Não foi possível consultar o andamento.'
        );
      }

      setFinancyStatus(result);

      if (
        result.assinatura?.url
      ) {
        setFinancyContratoUrl(
          result.assinatura.url
        );
      }

      // FINANCY_AVISO_ASSINATURA_NAO_CONFIRMADA
      if (
        result.etapa === 'COLETA'
      ) {
        setError('');
        setFinancyEtapa('COLETA');

      } else if (
        result.etapa === 'PAGAMENTO'
      ) {
        setError('');

        setFinancyEtapa(
          'PAGAMENTO'
        );

      } else {
        setFinancyEtapa(
          'ASSINATURA'
        );

        setError(
          'A assinatura ainda não foi confirmada pela Assinafy. Conclua a assinatura no WhatsApp e depois clique novamente em “Já assinei — verificar assinatura”.'
        );
      }

    } catch (err) {
      setError(
        err.message ||
        'Não foi possível consultar o andamento.'
      );
    }
  }

  // FINANCY_POLLING_ASSINATURA
  useEffect(() => {
    if (
      !coletaFinancy ||
      financyEtapa !== 'ASSINATURA' ||
      !financyLeadId
    ) {
      return undefined;
    }

    const timer =
      setInterval(
        async () => {
          try {
            const response =
              await fetch(
                `${API}/partner-intake/financy/status/${financyLeadId}`
              );

            const result =
              await response.json();

            if (
              !response.ok
            ) {
              return;
            }

            setFinancyStatus(result);

            if (
              result.etapa === 'PAGAMENTO'
            ) {
              setError('');

              setFinancyEtapa(
                'PAGAMENTO'
              );
            }

            if (
              result.etapa === 'COLETA'
            ) {
              setError('');

              setFinancyEtapa(
                'COLETA'
              );
            }

          } catch (_error) {
            /*
             * Polling silencioso:
             * botão manual continua disponível.
             */
          }
        },
        5000
      );

    return () => {
      clearInterval(timer);
    };
  }, [
    coletaFinancy,
    financyEtapa,
    financyLeadId
  ]);

  async function submit() {
    if (!consent) {
      setError(
        'Confirme a autorização para o tratamento dos dados e execução do serviço.'
      );
      return;
    }

    const arquivos =
      Object.values(files).filter(Boolean);

    const arquivoGrande =
      arquivos.find(
        arquivo =>
          arquivo.size >
          20 * 1024 * 1024
      );

    if (arquivoGrande) {
      setError(
        `O arquivo "${arquivoGrande.name}" ultrapassa 20 MB. Tire outra foto ou envie um arquivo menor.`
      );
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
      return;
    }

    const tamanhoTotal =
      arquivos.reduce(
        (total, arquivo) =>
          total + arquivo.size,
        0
      );

    if (
      tamanhoTotal >
      120 * 1024 * 1024
    ) {
      setError(
        'O conjunto de documentos ultrapassa o limite permitido. Reduza o tamanho das fotos e tente novamente.'
      );
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
      return;
    }

    try {
      setSending(true);
      setError('');

      const body = new FormData();

      body.append(
        'payload',
        JSON.stringify(data)
      );

      Object.entries(files).forEach(
        ([key, value]) => {
          if (value) {
            body.append(
              key,
              value
            );
          }
        }
      );

      const response = await fetch(
        `${API}/partner-intake/${tokenColeta}`,
        {
          method: 'POST',
          body
        }
      );

      const respostaTexto =
        await response.text();

      let result = {};

      try {
        result =
          respostaTexto
            ? JSON.parse(respostaTexto)
            : {};
      } catch (_) {
        result = {};
      }

      if (response.status === 413) {
        throw new Error(
          'As fotos ultrapassaram o limite de envio. Reduza o tamanho dos arquivos e tente novamente.'
        );
      }

      if (!response.ok) {
        throw new Error(
          result.error ||
          'Não foi possível enviar os dados. Confira as informações e tente novamente.'
        );
      }

      setDone(result);

      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    } catch (err) {
      const mensagem =
        String(err?.message || '');

      if (
        /string did not match|failed to fetch|load failed/i.test(
          mensagem
        )
      ) {
        setError(
          'Não foi possível concluir o envio. Verifique sua internet e tente novamente.'
        );
      } else {
        setError(
          mensagem ||
          'Não foi possível concluir o envio.'
        );
      }

      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    } finally {
      setSending(false);
    }
  }

  if (loading) return <div className="min-h-screen bg-slate-950 flex items-center justify-center"><div className="w-10 h-10 border-4 border-slate-700 border-t-blue-500 rounded-full animate-spin" /></div>;

  if (done) return (
    <div className="min-h-screen bg-slate-100 px-4 py-12 flex items-start justify-center">
      <div className="w-full max-w-xl bg-white rounded-2xl shadow-xl p-8 text-center">
        <CheckCircle2 size={64} className="mx-auto text-green-500 mb-4" />
        <h1 className="text-2xl font-bold text-slate-900">Documentação recebida com sucesso!</h1>
        <p className="text-slate-600 mt-3">Os dados foram encaminhados com segurança para a equipe responsável.</p>
        <div className="mt-6 rounded-xl bg-slate-100 px-4 py-3 font-bold text-slate-700">Protocolo: {done.protocolo}</div>
        <p className="text-sm text-slate-500 mt-5">Você já pode fechar esta página.</p>
      </div>
    </div>
  );

  if (!partner) return <div className="min-h-screen bg-slate-950 text-white flex items-center justify-center px-4"><div className="max-w-md text-center"><ShieldOffIcon /><h1 className="text-xl font-bold mt-4">Link indisponível</h1><p className="text-slate-400 mt-2">{error || 'Este link é inválido ou foi desativado.'}</p></div></div>;

  if (
    coletaFinancy &&
    financyEtapa === 'CONTRATACAO'
  ) {
    return (
      <div className="min-h-screen bg-slate-100 px-4 py-8">
        <div className="max-w-2xl mx-auto bg-white rounded-2xl shadow-xl p-6 sm:p-8">
          <div className="text-center mb-7">
            <div className="text-2xl font-black text-slate-900">
              RATING
            </div>

            <p className="text-slate-500 mt-2">
              Contratação segura do serviço
            </p>
          </div>

          {error && (
            <div className="mb-5 rounded-xl border border-red-300 bg-red-50 px-4 py-3 text-red-700 font-medium">
              {error}
            </div>
          )}

          <h1 className="text-xl font-bold mb-5">
            1. Dados para contratação
          </h1>

          <Identification
            data={data}
            update={update}
          />

          <div className="mt-8 border-t pt-6">
            <h2 className="text-xl font-bold mb-4">
              2. Escolha a forma de pagamento
            </h2>

            <div className="grid gap-3">
              {[
                [
                  'CARTAO',
                  'Cartão de crédito',
                  data.tipo_pessoa === 'PF'
                    ? 'R$ 997,00 em até 3x sem juros'
                    : 'R$ 1.297,00 em até 3x sem juros'
                ],
                [
                  'PIX_FACILITADO',
                  'PIX Facilitado',
                  data.tipo_pessoa === 'PF'
                    ? 'R$ 498,50 agora + R$ 498,50 em 30 dias'
                    : 'R$ 648,50 agora + R$ 648,50 em 30 dias'
                ],
                [
                  'PIX_AVISTA',
                  'PIX à vista',
                  data.tipo_pessoa === 'PF'
                    ? 'R$ 800,00'
                    : 'R$ 1.097,00'
                ]
              ].map(
                ([valor, titulo, detalhe]) => (
                  <button
                    key={valor}
                    type="button"
                    onClick={() =>
                      setFinancyForma(valor)
                    }
                    className={
                      `text-left rounded-xl border-2 p-4 transition ${
                        financyForma === valor
                          ? 'border-blue-600 bg-blue-50'
                          : 'border-slate-200 bg-white'
                      }`
                    }
                  >
                    <div className="font-bold text-slate-900">
                      {titulo}
                    </div>

                    <div className="text-sm text-slate-600 mt-1">
                      {detalhe}
                    </div>
                  </button>
                )
              )}
            </div>
          </div>

          <button
            type="button"
            disabled={financyProcessando}
            onClick={iniciarContratacaoFinancy}
            className="mt-7 w-full rounded-xl bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white px-5 py-4 font-bold"
          >
            {financyProcessando
              ? 'Gerando contrato...'
              : 'Continuar para o contrato'}
          </button>

          <p className="text-center text-xs text-slate-500 mt-5">
            Seus dados são utilizados para gerar o contrato e executar o serviço contratado.
          </p>
        </div>
      </div>
    );
  }

  if (
    coletaFinancy &&
    (
      financyEtapa === 'ASSINATURA' ||
      financyEtapa === 'PAGAMENTO'
    )
  ) {
    const pagamento =
      financyStatus?.pagamento || {};

    return (
      <div className="min-h-screen bg-slate-100 px-4 py-10">
        <div className="max-w-xl mx-auto bg-white rounded-2xl shadow-xl p-6 sm:p-8 text-center">

          <div className="text-2xl font-black">
            RATING
          </div>

          {error && (
            <div className="mt-5 rounded-xl border border-red-300 bg-red-50 px-4 py-3 text-red-700">
              {error}
            </div>
          )}

          {financyEtapa === 'ASSINATURA' ? (
            <>
              <h1 className="text-2xl font-bold mt-7">
                Contrato gerado
              </h1>

              {/* FINANCY_AVISO_WHATSAPP_ASSINATURA */}
              <div className="mt-5 rounded-xl border border-blue-200 bg-blue-50 px-5 py-5 text-left">
                <div className="font-bold text-blue-900 text-lg">
                  Abra seu WhatsApp
                </div>

                <p className="text-blue-800 mt-2">
                  A Assinafy enviou o contrato para assinatura pelo WhatsApp.
                  Abra a conversa da Assinafy, confira os dados e conclua a assinatura.
                </p>

                <p className="text-blue-800 mt-2 font-semibold">
                  Depois de assinar, volte a esta página para continuar.
                </p>
              </div>

              <button
                type="button"
                onClick={consultarStatusFinancy}
                className="mt-5 w-full rounded-xl bg-blue-600 hover:bg-blue-700 text-white px-5 py-4 font-bold"
              >
                Já assinei — verificar assinatura
              </button>
            </>
          ) : (
            <>
              <CheckCircle2
                size={56}
                className="mx-auto text-green-500 mt-7"
              />

              <h1 className="text-2xl font-bold mt-4">
                Contrato assinado
              </h1>

              <div className="mt-4 rounded-xl border border-green-200 bg-green-50 px-4 py-3 text-green-800 font-semibold">
                Contrato assinado com sucesso.
              </div>

              <p className="text-slate-700 mt-4 font-semibold">
                Agora efetue o pagamento abaixo para liberar a próxima etapa.
              </p>

              <p className="text-slate-500 mt-2 text-sm">
                Assim que o pagamento for confirmado, a coleta de informações e documentos será liberada automaticamente.
              </p>

              {pagamento.cartao_url && (
                <a
                  href={pagamento.cartao_url}
                  target="_blank"
                  rel="noreferrer"
                  className="block mt-6 rounded-xl bg-blue-600 text-white px-5 py-4 font-bold"
                >
                  Pagar com cartão
                </a>
              )}

              {pagamento.pix_copia_cola && (
                <div className="mt-6 text-left">
                  <div className="font-bold mb-2">
                    PIX copia e cola
                  </div>

                  <div className="rounded-xl bg-slate-100 p-4 text-xs break-all select-all">
                    {pagamento.pix_copia_cola}
                  </div>
                </div>
              )}

              {!pagamento.cartao_url &&
               !pagamento.pix_copia_cola && (
                <div className="mt-6 rounded-xl bg-amber-50 border border-amber-200 p-4 text-amber-800">
                  Estamos preparando os dados para pagamento.
                </div>
              )}

              <button
                type="button"
                onClick={consultarStatusFinancy}
                className="mt-5 w-full rounded-xl bg-green-600 text-white px-5 py-4 font-bold"
              >
                Já paguei — verificar pagamento
              </button>
            </>
          )}

          <p className="text-xs text-slate-500 mt-6">
            Após a confirmação do pagamento, a coleta completa será liberada automaticamente.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-100 text-slate-900">
      <header className="bg-slate-950 text-white px-4 py-5 shadow-lg">
        <div className="max-w-4xl mx-auto flex items-center justify-between gap-4">
          <div><div className="text-xl font-black tracking-wide">RATING</div><div className="text-sm text-slate-400">Coleta segura de informações</div></div>
          <div className="text-right">
              <div className="text-xs text-slate-400">
                {coletaFinancy
                  ? 'Atendimento oficial'
                  : 'Encaminhado por'}
              </div>
              <div className="font-bold flex items-center gap-1.5">
                <Building2 size={16} />
                {partner}
              </div>
            </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-7">
        <div className="mb-6 overflow-x-auto"><div className="flex min-w-[650px] gap-2">
          {steps.map((label, index) => <div key={label} className={`flex-1 rounded-lg px-3 py-2 text-center text-xs font-bold ${index === step ? 'bg-blue-600 text-white' : index < step ? 'bg-green-100 text-green-700' : 'bg-white text-slate-400'}`}>{index + 1}. {label}</div>)}
        </div></div>

        {error && <div className="mb-5 rounded-xl border border-red-300 bg-red-50 px-4 py-3 text-red-700 font-medium">{error}</div>}

        <section className="bg-white rounded-2xl shadow-sm border border-slate-200 p-5 sm:p-8">
          {step === 0 && <Identification data={data} update={update} />}
          {step === 1 && <Documents data={data} update={update} files={files} setFiles={setFiles} />}
          {step === 2 && <Personal data={data} update={update} files={files} setFiles={setFiles} />}
          {step === 3 && <AddressAssets data={data} update={update} updateArray={updateArray} addProperty={addProperty} addVehicle={addVehicle} removeItem={removeItem} />}
          {step === 4 && <Review data={data} review={review} files={files} consent={consent} setConsent={setConsent} />}

          <div className="flex justify-between gap-3 mt-8 pt-5 border-t border-slate-200">
            <button type="button" onClick={() => { setError(''); setStep(current => Math.max(0, current - 1)); }} disabled={step === 0 || sending} className="rounded-xl border border-slate-300 px-4 py-3 font-semibold disabled:opacity-30 flex items-center gap-2"><ArrowLeft size={18} /> Voltar</button>
            {step < 4 ? <button type="button" onClick={next} className="rounded-xl bg-blue-600 hover:bg-blue-700 text-white px-5 py-3 font-bold flex items-center gap-2">Continuar <ArrowRight size={18} /></button> : <button type="button" onClick={submit} disabled={sending} className="rounded-xl bg-green-600 hover:bg-green-700 disabled:opacity-50 text-white px-5 py-3 font-bold flex items-center gap-2"><Send size={18} /> {sending ? 'Enviando...' : 'Enviar documentação'}</button>}
          </div>
        </section>
        <p className="text-center text-xs text-slate-500 mt-5 flex items-center justify-center gap-1"><Lock size={14} /> Envie somente por esta página segura em painel.financyrating.tech.</p>
      </main>
    </div>
  );
}

function Identification({ data, update }) {
  return <div><h1 className="text-2xl font-bold mb-1">Identificação e contato</h1><p className="text-slate-500 mb-6">Preencha os dados da pessoa que receberá o serviço.</p><div className="grid sm:grid-cols-2 gap-5">
    <Field label="Tipo de atendimento" required><select value={data.tipo_pessoa} onChange={e => update('tipo_pessoa', e.target.value)} className={inputClass}><option value="PF">Pessoa Física</option><option value="PJ">Pessoa Jurídica</option><option value="DUPLO">CPF e CNPJ</option></select></Field>
    <Field label={data.tipo_pessoa !== 'PF' ? 'Nome do sócio administrador' : 'Nome completo'} required><input value={data.nome} onChange={e => update('nome', e.target.value)} className={inputClass} /></Field>
    {data.tipo_pessoa !== 'PF' && <><Field label="Razão social"><input value={data.razao_social} onChange={e => update('razao_social', e.target.value)} className={inputClass} /></Field><Field label="Nome fantasia"><input value={data.nome_fantasia} onChange={e => update('nome_fantasia', e.target.value)} className={inputClass} /></Field></>}
    <Field label={data.tipo_pessoa !== 'PF' ? 'CNPJ' : 'CPF'} required><input inputMode="numeric" value={data.cpf_cnpj} onChange={e => update('cpf_cnpj', e.target.value)} className={inputClass} /></Field>
    {data.tipo_pessoa !== 'PF' && <Field label="CPF do sócio administrador" required><input inputMode="numeric" value={data.cpf_socio} onChange={e => update('cpf_socio', e.target.value)} className={inputClass} /></Field>}
    <Field label="Celular com DDD" required><input inputMode="tel" value={data.telefone_principal} onChange={e => update('telefone_principal', e.target.value)} placeholder="(62) 99999-9999" className={inputClass} /></Field>
    <Field label="E-mail" required><input type="email" value={data.email} onChange={e => update('email', e.target.value)} className={inputClass} /></Field>
  </div></div>;
}

function FileInput({
  label,
  name,
  files,
  setFiles,
  required,
  help
}) {
  const captureMode =
    name === 'selfie_documento'
      ? 'user'
      : 'environment';

  const selecionarArquivo = event => {
    const arquivo =
      event.target.files?.[0] ||
      null;

    if (
      arquivo &&
      arquivo.size >
        20 * 1024 * 1024
    ) {
      window.alert(
        'Este arquivo ultrapassa 20 MB. Escolha uma foto ou arquivo menor.'
      );

      event.target.value = '';
      return;
    }

    setFiles(current => ({
      ...current,
      [name]: arquivo
    }));
  };

  return (
    <Field
      label={label}
      required={required}
      help={help}
    >
      <div className="grid grid-cols-2 gap-3">
        <label className="cursor-pointer rounded-xl border border-blue-200 bg-blue-50 px-3 py-3 text-center text-sm font-bold text-blue-700">
          📷 Tirar foto

          <input
            type="file"
            accept="image/*"
            capture={captureMode}
            onChange={selecionarArquivo}
            className="hidden"
          />
        </label>

        <label className="cursor-pointer rounded-xl border border-slate-300 bg-white px-3 py-3 text-center text-sm font-bold text-slate-700">
          📎 Enviar arquivo

          <input
            type="file"
            accept=".pdf,.jpg,.jpeg,.png,.webp"
            onChange={selecionarArquivo}
            className="hidden"
          />
        </label>
      </div>

      {files[name] && (
        <span className="block text-xs text-green-600 mt-2 break-all">
          Selecionado: {files[name].name}
        </span>
      )}
    </Field>
  );
}

function Documents({ data, update, files, setFiles }) {
  return <div><h1 className="text-2xl font-bold mb-1">Documentos</h1><p className="text-slate-500 mb-6">Fotos legíveis, sem cortes e sem reflexos. Cada arquivo pode ter até 20 MB.</p><div className="grid sm:grid-cols-2 gap-5">
    <Field label="Documento utilizado" required><select value={data.doc_tipo} onChange={e => update('doc_tipo', e.target.value)} className={inputClass}><option value="CNH">CNH</option><option value="RG">RG / Identidade</option></select></Field>
    {data.doc_tipo === 'CNH' && <Field label="Número do RG" help="Se não tiver essa informação, deixe em branco."><input value={data.rg_numero} onChange={e => update('rg_numero', e.target.value)} className={inputClass} /></Field>}
    {data.doc_tipo === 'CNH' && <Field label="Estado do RG"><input value={data.estado_rg} onChange={e => update('estado_rg', e.target.value)} className={inputClass} /></Field>}
    {data.doc_tipo === 'CNH' && <Field label="Data de expedição do RG" help="Data no formato dia/mês/ano."><input type="date" lang="pt-BR" value={data.expedicao_rg} onChange={e => update('expedicao_rg', e.target.value)} className={inputClass} /></Field>}
    <FileInput label={`Frente da ${data.doc_tipo}`} name="documento_frente" files={files} setFiles={setFiles} required />
    {data.doc_tipo === 'RG' && <FileInput label="Verso do RG" name="documento_verso" files={files} setFiles={setFiles} required />}
    <FileInput label="Selfie segurando o documento" name="selfie_documento" files={files} setFiles={setFiles} required />
    {data.doc_tipo === 'RG' && <Field label="O CPF aparece no RG?" required><select value={data.rg_tem_cpf} onChange={e => update('rg_tem_cpf', e.target.value)} className={inputClass}><option value="sim">Sim</option><option value="nao">Não</option></select></Field>}
    {data.doc_tipo === 'RG' && data.rg_tem_cpf === 'nao' && <FileInput label="Documento do CPF" name="cpf_documento" files={files} setFiles={setFiles} required />}
    <FileInput label="Comprovante de endereço" name="comprovante_endereco" files={files} setFiles={setFiles} required help="Preferencialmente dos últimos 90 dias." />
  </div></div>;
}

function Personal({ data, update, files, setFiles }) {
  const married = data.estado_civil_texto === 'Casado';
  return <div><h1 className="text-2xl font-bold mb-1">Dados pessoais e profissionais</h1><p className="text-slate-500 mb-6">Informe os dados como constam nos documentos.</p><div className="grid sm:grid-cols-2 gap-5">
    <Field label="Senha SERASA" required help="Dado operacional usado somente para executar o serviço."><input type="text" autoComplete="off" autoCapitalize="none" spellCheck="false" value={data.senha_serasa} onChange={e => update('senha_serasa', e.target.value)} className={inputClass} /></Field>
    <Field label="Título de eleitor"><input value={data.titulo_eleitor} onChange={e => update('titulo_eleitor', e.target.value)} className={inputClass} /></Field>
    <Field label="Sexo"><select value={data.sexo} onChange={e => update('sexo', e.target.value)} className={inputClass}><option value="">Selecione</option><option>Masculino</option><option>Feminino</option><option>Outro</option><option>Prefiro não informar</option></select></Field>
    <Field label="Data de nascimento" help="Data no formato dia/mês/ano."><input type="date" lang="pt-BR" value={data.data_nascimento} onChange={e => update('data_nascimento', e.target.value)} className={inputClass} /></Field>
    <Field label="Nome do pai"><input value={data.nome_pai} onChange={e => update('nome_pai', e.target.value)} className={inputClass} /></Field>
    <Field label="Nome da mãe"><input value={data.nome_mae} onChange={e => update('nome_mae', e.target.value)} className={inputClass} /></Field>
    <Field label="Grau de instrução" required><select value={data.escolaridade} onChange={e => update('escolaridade', e.target.value)} className={inputClass}><option value="">Selecione</option><option>Ensino Fundamental</option><option>Ensino Médio</option><option>Superior Incompleto</option><option>Superior Completo</option><option>Pós-graduação</option></select></Field>
    <Field label="Profissão" required><input value={data.profissao} onChange={e => update('profissao', e.target.value)} className={inputClass} /></Field>
    <Field label="Média salarial" required><input inputMode="decimal" value={data.media_salarial} onChange={e => update('media_salarial', e.target.value)} placeholder="Ex.: 5.000,00" className={inputClass} /></Field>
    <Field label="Dia de recebimento"><input inputMode="numeric" maxLength={2} value={data.dia_pagamento} onChange={e => update('dia_pagamento', e.target.value)} className={inputClass} /></Field>
    <Field label="Estado civil" required><select value={data.estado_civil_texto} onChange={e => update('estado_civil_texto', e.target.value)} className={inputClass}><option value="">Selecione</option><option>Solteiro</option><option>Casado</option><option>Divorciado</option><option>Separado</option><option>Viúvo</option><option>Outro</option></select></Field>
    <Field label="Dependentes"><input type="number" min="0" value={data.dependentes} onChange={e => update('dependentes', e.target.value)} className={inputClass} /></Field>
    {married && <><Field label="Nome do cônjuge" required><input value={data.conjuge_nome} onChange={e => update('conjuge_nome', e.target.value)} className={inputClass} /></Field><Field label="CPF do cônjuge" required><input inputMode="numeric" value={data.conjuge_cpf} onChange={e => update('conjuge_cpf', e.target.value)} className={inputClass} /></Field><Field label="Nascimento do cônjuge" help="Data no formato dia/mês/ano."><input type="date" lang="pt-BR" value={data.conjuge_nascimento} onChange={e => update('conjuge_nascimento', e.target.value)} className={inputClass} /></Field><FileInput label="Certidão de casamento" name="certidao_casamento" files={files} setFiles={setFiles} required /></>}
  </div></div>;
}

function AddressAssets({ data, update, updateArray, addProperty, addVehicle, removeItem }) {
  return <div><h1 className="text-2xl font-bold mb-1">Endereço e patrimônio</h1><p className="text-slate-500 mb-6">Você poderá adicionar todos os imóveis e veículos antes de enviar.</p><div className="grid sm:grid-cols-2 gap-5">
    <Field label="CEP" required><input inputMode="numeric" value={data.cep} onChange={e => update('cep', e.target.value)} className={inputClass} /></Field>
    <Field label="Endereço residencial" required><input value={data.endereco} onChange={e => update('endereco', e.target.value)} placeholder="Rua, número e complemento" className={inputClass} /></Field>
    <Field label="Bairro"><input value={data.bairro} onChange={e => update('bairro', e.target.value)} className={inputClass} /></Field>
    <Field label="Município" required><input value={data.municipio} onChange={e => update('municipio', e.target.value)} className={inputClass} /></Field>
    <Field label="Estado" required><input value={data.estado} onChange={e => update('estado', e.target.value)} className={inputClass} /></Field>
    <Field label="Telefone residencial"><input inputMode="tel" value={data.telefone_residencial} onChange={e => update('telefone_residencial', e.target.value)} className={inputClass} /></Field>
    <Field label="Telefone comercial"><input inputMode="tel" value={data.telefone_comercial} onChange={e => update('telefone_comercial', e.target.value)} className={inputClass} /></Field>
    <Field label="Outros telefones"><input value={data.outros_telefones_numeros} onChange={e => update('outros_telefones_numeros', e.target.value)} className={inputClass} /></Field>
    <Field label="Tipo de imóvel" required>
      <select
        value={data.situacao_imovel}
        onChange={e =>
          update(
            'situacao_imovel',
            e.target.value
          )
        }
        className={inputClass}
      >
        <option value="">Selecione</option>
        <option value="Própria quitada">
          Própria quitada
        </option>
        <option value="Alugada">
          Alugada
        </option>
        <option value="Moro com familiares">
          Moro com familiares
        </option>
        <option value="Cedida pela empresa">
          Cedida pela empresa
        </option>
        <option value="Cedida por familiares">
          Cedida por familiares
        </option>
        <option value="Própria financiada">
          Própria financiada
        </option>
        <option value="Outros">
          Outros
        </option>
      </select>
    </Field>
    {data.situacao_imovel &&
      ![
        'Própria quitada',
        'Própria financiada'
      ].includes(
        data.situacao_imovel
      ) &&
      <Field label="Localização da residência atual">
        <input
          value={
            data.localizacao_imovel_alugado
          }
          onChange={e =>
            update(
              'localizacao_imovel_alugado',
              e.target.value
            )
          }
          className={inputClass}
        />
      </Field>}

    <Field label="Possui imóvel em seu nome?" required>
      <select
        value={data.possui_imoveis}
        onChange={e => {
          const valor = e.target.value;

          update(
            'possui_imoveis',
            valor
          );

          if (
            valor === 'sim' &&
            data.imoveis.length === 0
          ) {
            addProperty();
          }
        }}
        className={inputClass}
      >
        <option value="nao">
          Não
        </option>

        <option value="sim">
          Sim
        </option>
      </select>
    </Field>
  </div>
  {data.possui_imoveis === 'sim' && <DynamicList title="Imóveis" items={data.imoveis} add={addProperty} remove={index => removeItem('imoveis', index)}>{(item, index) => <div className="grid sm:grid-cols-3 gap-3"><Field label="Tipo"><select value={item.tipo} onChange={e => updateArray('imoveis', index, 'tipo', e.target.value)} className={inputClass}><option>Casa</option><option>Apartamento</option><option>Comercial</option><option>Terreno</option></select></Field><Field label="Cidade/Estado"><input value={item.localizacao} onChange={e => updateArray('imoveis', index, 'localizacao', e.target.value)} className={inputClass} /></Field><Field label="Valor aproximado"><input value={item.valor} onChange={e => updateArray('imoveis', index, 'valor', e.target.value)} className={inputClass} /></Field></div>}</DynamicList>}
  <DynamicList title="Veículos (opcional)" items={data.veiculos} add={addVehicle} remove={index => removeItem('veiculos', index)}>{(item, index) => <div className="grid sm:grid-cols-5 gap-3"><Field label="Tipo"><select value={item.tipo} onChange={e => updateArray('veiculos', index, 'tipo', e.target.value)} className={inputClass}><option>Carro</option><option>Moto</option><option>Caminhão</option><option>Outro</option></select></Field><Field label="Valor"><input value={item.valor} onChange={e => updateArray('veiculos', index, 'valor', e.target.value)} className={inputClass} /></Field><Field label="Ano"><input value={item.ano} onChange={e => updateArray('veiculos', index, 'ano', e.target.value)} className={inputClass} /></Field><Field label="Placa"><input value={item.placa} onChange={e => updateArray('veiculos', index, 'placa', e.target.value.toUpperCase())} className={inputClass} /></Field><Field label="Estado placa"><input value={item.estado_placa} onChange={e => updateArray('veiculos', index, 'estado_placa', e.target.value)} className={inputClass} /></Field></div>}</DynamicList>
  </div>;
}

function DynamicList({ title, items, add, remove, children }) {
  return <div className="mt-7 border-t border-slate-200 pt-6"><div className="flex items-center justify-between gap-3 mb-4"><h2 className="text-lg font-bold">{title}</h2><button type="button" onClick={add} className="rounded-lg bg-blue-50 text-blue-700 px-3 py-2 text-sm font-bold flex items-center gap-1"><Plus size={16} /> Adicionar</button></div><div className="space-y-4">{items.map((item, index) => <div key={index} className="rounded-xl border border-slate-200 bg-slate-50 p-4"><div className="flex justify-between mb-3"><strong>Item {index + 1}</strong><button type="button" onClick={() => remove(index)} className="text-red-500"><Trash2 size={18} /></button></div>{children(item, index)}</div>)}</div>{items.length === 0 && <p className="text-sm text-slate-500">Nenhum item adicionado.</p>}</div>;
}

function Review({ data, review, files, consent, setConsent }) {
  return <div><h1 className="text-2xl font-bold mb-1">Confira antes de enviar</h1><p className="text-slate-500 mb-6">Se algo estiver incorreto, use Voltar e altere o campo.</p><div className="grid sm:grid-cols-2 gap-x-8 gap-y-3">{review.map(([label, value]) => <div key={label} className="border-b border-slate-100 pb-2"><div className="text-xs text-slate-500">{label}</div><div className="font-semibold break-words">{value || 'Não informado'}</div></div>)}</div>
    <div className="mt-7"><h2 className="font-bold mb-3">Arquivos selecionados</h2><div className="grid sm:grid-cols-2 gap-2">{Object.entries(files).filter(([, file]) => file).map(([key, file]) => <div key={key} className="rounded-lg bg-green-50 text-green-800 px-3 py-2 text-sm flex items-center gap-2"><FileCheck2 size={16} /> {file.name}</div>)}</div></div>
    <div className="mt-7"><h2 className="font-bold mb-3">Patrimônio informado</h2><div className="text-sm text-slate-600 space-y-2">{data.imoveis.map((item, index) => <div key={`i-${index}`}>Imóvel {index + 1}: {item.tipo} · {item.localizacao} · R$ {item.valor}</div>)}{data.veiculos.map((item, index) => <div key={`v-${index}`}>Veículo {index + 1}: {item.tipo} · {item.placa} · {item.ano} · R$ {item.valor}</div>)}{!data.imoveis.length && !data.veiculos.length && <div>Nenhum patrimônio adicionado.</div>}</div></div>
    <label className="mt-7 flex items-start gap-3 rounded-xl border border-blue-200 bg-blue-50 p-4"><input type="checkbox" checked={consent} onChange={e => setConsent(e.target.checked)} className="mt-1 h-4 w-4" /><span className="text-sm text-slate-700">Confirmo que os dados são verdadeiros e autorizo seu tratamento pela FINANCY para cadastro e execução do serviço solicitado. Estou ciente de que informações sensíveis foram enviadas por conexão segura.</span></label>
  </div>;
}

function ShieldOffIcon() { return <div className="mx-auto w-14 h-14 rounded-full bg-red-500/10 text-red-400 flex items-center justify-center"><Lock size={28} /></div>; }
